<template>
  <Button v-bind="filteredProps" :class="btnClasses" :style="btnStyles" :loading="loading" :severity="computedSeverity"
    :outlined="outlined" :size="computedSize">
    <slot />
  </Button>
</template>

<script setup lang="ts">
import Button from 'primevue/button'
import { computed, useAttrs } from 'vue'

import { configService } from '@/common/utils/clientConfig'

interface Props {
  block?: boolean
  outlined?: boolean
  depressed?: boolean // Аналог text в PrimeVue
  loading?: boolean
  small?: boolean
  large?: boolean
  color?: string // 'primary' | 'secondary' | 'success' | 'warning' | 'danger'
}

const props = withDefaults(defineProps<Props>(), {
  block: false,
  outlined: false,
  depressed: false,
  loading: false,
  small: false,
  large: false,
  color: ''
})

const attrs = useAttrs()

// Фильтруем props чтобы не передавать их как атрибуты
const filteredProps = computed(() => {
  const { block, outlined, depressed, loading, small, large, color, ...rest } = props
  return { ...rest, ...attrs }
})

const computedSeverity = computed(() => {
  if (props.color === 'primary') return undefined // primary - дефолтный в PrimeVue
  if (props.color === 'secondary') return 'secondary'
  if (props.color === 'success') return 'success'
  if (props.color === 'warning') return 'warning'
  if (props.color === 'danger') return 'danger'
  return undefined
})

const computedSize = computed(() => {
  if (props.small) return 'small'
  if (props.large) return 'large'
  return undefined
})

const btnStyles = computed(() => ({
  '--primary-color': configService.get('THEME_ACCENT'),
  '--primary-light': configService.get('THEME_ACCENT'),
  '--primary-dark': configService.get('THEME_ACCENT'),
  width: props.block ? '100%' : undefined,
}))

const btnClasses = computed(() => [
  'm-button',
  `m-button--${props.color}`,
  { 'm-button--block': props.block }
])
</script>

<style scoped lang="scss">
.m-button {
  font-weight: 500;
  letter-spacing: 0.02em;
  border-radius: 6px;

  // Кастомные стили для primary кнопки
  &--primary {
    background: var(--primary-color) !important;
    border: 1px solid color-mix(in srgb, var(--primary-color) 85%, black) !important;

    &:hover:not(.p-disabled) {
      background: color-mix(in srgb, var(--primary-color) 92%, black) !important;
    }

    // Для outlined версии
    &.p-button-outlined {
      background: transparent !important;
      color: var(--primary-color) !important;
      border: 1px solid var(--primary-color) !important;

      &:hover:not(.p-disabled) {
        background: var(--primary-color) !important;
        color: white !important;
      }
    }
  }

  &--block {
    width: 100%;
  }
}

:deep(.p-button) {
  &.m-button {
    border-radius: 6px;
  }
}
</style>
